import { useState, useEffect, useCallback, useRef } from 'react';
import type { Card as CardType, ShinyTier } from '@flip7/shared';
import { isActionCard } from '@flip7/shared';
import { Card } from './Card';

interface FlippableCardProps {
  card: CardType;
  /** Start face-down and flip to reveal */
  initialFaceDown: boolean;
  /** Delay before flip starts (ms) */
  faceDownHold?: number;
  /** Called when flip animation finishes */
  onFlipComplete?: () => void;
  /** Show gold glow emphasis after flip (for action cards) */
  emphasis?: boolean;
  /** Shiny tier to display */
  shinyTier?: ShinyTier;
}

/**
 * Wraps a Card with a 3D flip animation.
 * Shows card back face-down, then flips to reveal the actual card.
 */
export function FlippableCard({
  card,
  initialFaceDown,
  faceDownHold = 300,
  onFlipComplete,
  emphasis,
  shinyTier,
}: FlippableCardProps) {
  const [flipped, setFlipped] = useState(!initialFaceDown);
  const [showEmphasis, setShowEmphasis] = useState(false);
  const completeFired = useRef(false);

  // Check reduced motion preference
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (!initialFaceDown || prefersReducedMotion.current) {
      setFlipped(true);
      return;
    }
    const timer = setTimeout(() => setFlipped(true), faceDownHold);
    return () => clearTimeout(timer);
  }, [initialFaceDown, faceDownHold]);

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.propertyName !== 'transform') return;
    if (completeFired.current) return;
    completeFired.current = true;

    if (emphasis || (isActionCard(card) && initialFaceDown)) {
      setShowEmphasis(true);
    }
    onFlipComplete?.();
  }, [emphasis, card, initialFaceDown, onFlipComplete]);

  // For reduced motion, fire onFlipComplete immediately
  useEffect(() => {
    if (prefersReducedMotion.current && initialFaceDown && !completeFired.current) {
      completeFired.current = true;
      onFlipComplete?.();
    }
  }, [initialFaceDown, onFlipComplete]);

  const innerClass = [
    'flippable-card__inner',
    flipped ? 'flippable-card__inner--flipped' : '',
  ].filter(Boolean).join(' ');

  const wrapperClass = [
    'flippable-card',
    showEmphasis ? 'flippable-card--emphasis' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass}>
      <div className={innerClass} onTransitionEnd={handleTransitionEnd}>
        {/* Card back */}
        <div className="flippable-card__back">
          <div className="flippable-card__back-pattern">
            <div className="flippable-card__back-inner" />
          </div>
        </div>

        {/* Card front */}
        <div className="flippable-card__front">
          <Card card={card} shinyTier={shinyTier} />
        </div>
      </div>
    </div>
  );
}
